import {
    ActivityCategory,
    activityCategoryLabels,
    ActivityRecordType,
    TaskTypeId,
} from "@sp-crm/core";
import { Select } from "components/ui/select";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import {
    activityTypeChoicesForRecordType,
    ActivityTypeOption,
    byActivityTypeName,
    isReferralSourceRecordType,
    noActivityTypeKey,
    unscopedVisitTypeChoices,
    useActivityTypeOptions,
} from "./activity-type-options";
import { useAvailableActivityCategories } from "./use-activity-categories";

interface ActivityTypeSelectProps {
    value: TaskTypeId | null;
    recordType: ActivityRecordType | null;
    onChange: (value: TaskTypeId | null) => void;
}

export const ActivityTypeSelect: React.FC<ActivityTypeSelectProps> = props => {
    const { value, recordType, onChange } = props;
    const options = useActivityTypeOptions();
    const availableCategories = useAvailableActivityCategories();
    const referralDetailVNext = useFeature("referralDetailVNext");
    const retainedVisitChoices = (): ActivityTypeOption[] =>
        options.filter(option => option.category === "visit" && option.id === value);
    const choicesFor = (category: ActivityCategory): ActivityTypeOption[] =>
        activityTypeChoicesForRecordType(options, category, recordType, value);
    const visitsRetainedOnly =
        referralDetailVNext &&
        recordType !== null &&
        isReferralSourceRecordType(recordType);
    const visitChoices = (): ActivityTypeOption[] => {
        if (visitsRetainedOnly) {
            return retainedVisitChoices();
        }
        if (referralDetailVNext && recordType === null) {
            return unscopedVisitTypeChoices(options, value);
        }
        return choicesFor("visit");
    };

    return (
        <Select
            label="Activity type"
            value={value ?? noActivityTypeKey}
            onChange={e =>
                onChange(
                    e.target.value === noActivityTypeKey
                        ? null
                        : (e.target.value as TaskTypeId),
                )
            }>
            <option value={noActivityTypeKey}>(none)</option>
            {availableCategories.map(category => {
                const choices = (
                    category === "visit" ? visitChoices() : choicesFor(category)
                ).sort(byActivityTypeName);
                if (choices.length === 0) {
                    return null;
                }
                return (
                    <optgroup key={category} label={activityCategoryLabels[category]}>
                        {choices.map(option => (
                            <option
                                key={option.id}
                                value={option.id}
                                disabled={category === "visit" && visitsRetainedOnly}>
                                {option.name}
                            </option>
                        ))}
                    </optgroup>
                );
            })}
        </Select>
    );
};
